import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./components/ui/button";
import { Textarea } from "./components/ui/textarea";
import { Loader2, RotateCcw, Save, FileText } from "lucide-react";
import { DeclarationText } from "./DeclarationText";

// Global Settings section: edit the registration step 3 declaration.
export const DeclarationSettings = ({ isOpen }) => {
  const [text, setText] = useState("");
  const [initial, setInitial] = useState("");
  const [defaultText, setDefaultText] = useState("");
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const [sample, setSample] = useState({ year: new Date().getFullYear(), fee: "" });

  useEffect(() => {
    if (!isOpen) return;
    axios.get("/settings/membership-year-options").then((res) => {
      const o = (res.data?.options || [])[0];
      if (o) setSample({ year: o.year, fee: o.fee == null ? "" : Number(o.fee).toFixed(2).replace(/\.00$/, "") });
    }).catch(() => {});
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    axios.get("/settings/membership-declaration").then((res) => {
      setText(res.data?.text || "");
      setInitial(res.data?.text || "");
      setDefaultText(res.data?.default || "");
    }).catch(() => toast.error("Could not load the membership declaration."));
  }, [isOpen]);

  const save = async () => {
    const clean = text.trim();
    if (!clean) return toast.error("Declaration text cannot be empty.");
    setSaving(true);
    try {
      await axios.put("/settings/membership-declaration", { text: clean });
      setInitial(clean);
      toast.success("Membership declaration saved. New registrations will see it right away.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save the declaration.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="space-y-3 pt-4 border-t border-slate-200" data-testid="declaration-settings">
      <div>
        <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
          <FileText className="h-4 w-4 text-blue-700" /> Membership request declaration (registration step 3)
        </h4>
        <p className="text-xs text-slate-500">
          <code>{"{year}"}</code> and <code>{"{fee}"}</code> are filled in automatically. A line in CAPITALS is a heading,
          a line starting with <code>- </code> is a bullet, and <code>[text](https://link)</code> makes a link.
        </p>
      </div>
      {preview ? (
        <div className="max-h-72 overflow-y-auto rounded-md border border-slate-200 p-4" data-testid="declaration-preview">
          <DeclarationText text={text} year={sample.year} fee={sample.fee} />
        </div>
      ) : (
        <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={10} className="text-xs font-mono" data-testid="declaration-textarea" />
      )}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-1">
          <Button type="button" variant="ghost" size="sm" className="h-7 text-slate-600" onClick={() => setPreview(!preview)} data-testid="declaration-preview-toggle">
            {preview ? "Edit" : "Preview"}
          </Button>
          <Button type="button" variant="ghost" size="sm" className="h-7 text-slate-600" onClick={() => setText(defaultText)} data-testid="declaration-reset-default">
            <RotateCcw className="h-3 w-3 mr-1" /> Reset to default
          </Button>
        </div>
        <Button onClick={save} disabled={saving || text === initial} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-8" data-testid="declaration-save">
          {saving ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Saving…</> : <><Save className="h-3.5 w-3.5 mr-1" /> Save declaration</>}
        </Button>
      </div>
    </section>
  );
};
